import { memo } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import Card from "../common/components/Card";
import { MaterialInput } from "../common/components/materialInput/MaterialInput";
import { type MessageProps } from "../common/components/Messages/Message";
import { IconType } from "../common/components/icon/IconType";
import { registerUser } from "./api/registerUserForFree";
import { redirectToUrl } from "../utils/url/urlUtils";
import { Link } from "../common/components/Link";
import LoginPopup from "../Login/components/LoginPopup";
import { useBooleanState } from "../common/hooks/useBooleanState";
import ResetPasswordPopup from "../Login/components/ResetPasswordPopup";
import RecaptchaWidget from "../common/components/RecaptchaWidget/RecaptchaWidget";
import { CAPTCHA_ENABLED, CAPTCHA_SERVER_ERROR, EMAIL_PATTERN } from "../utils/constants";
import { MediasRegistrationBanner } from "./components/MediasRegistrationBanner/MediasRegistrationBanner";
import { trackRegisterForFree } from "../feature/Analytics/ga/ga";
import { Controller, type FieldError, useForm } from "react-hook-form";
import { MarketingConsentCheckbox } from "./components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { TERMS_OF_USE_PAGE } from "../utils/url/staticUrls";
import { Trans, useTranslation } from "react-i18next";
import { CheckboxWithLabel } from "../common/components/CheckboxWithLabel";
import { useIsMinDesktopSizeM } from "../common/hooks/useMediaQuery";
import { Button } from "~/common/components/Button/Button";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Box } from "@mui/material";
import { CustomerRegistrationBanner } from "~/RegisterForFreePage/components/CustomerRegistrationBanner/CustomerRegistrationBanner";
import { Alert } from "~/common/m3/Alert/Alert";

interface FormFields {
  email: string;
  captcha: string;
  generalTerms: boolean;
  privacyPolicy: boolean;
  marketingConsent: boolean;
}

const RegisterForFreeDesktop = ({ showCustomerRegistration }: { showCustomerRegistration?: boolean }) => {
  const { t } = useTranslation();
  const {
    register,
    setError,
    formState: { errors },
    watch,
    setValue,
    control,
    handleSubmit,
  } = useForm<FormFields>({
    defaultValues: {
      email: "",
      captcha: "",
      generalTerms: false,
      privacyPolicy: false,
      marketingConsent: false,
    },
  });

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);

  const setSubmissionError = (errorString: string) => {
    if (errorString == CAPTCHA_SERVER_ERROR) {
      setError("captcha", { message: errorString });
    } else {
      setError("email", { message: errorString });
    }
  };

  const onSubmitHandler = handleSubmit(async (values) => {
    trackRegisterForFree("Register for Free");
    const response = await registerUser(values);
    if (response.error) {
      setSubmissionError(response.error);
    } else {
      redirectToUrl("/registered/user/confirmation");
    }
  });

  const onResetClick = (email: string) => {
    setValue("email", email);
    closeLogin();
    openReset();
  };

  const isLargeDesktop = useIsMinDesktopSizeM();

  const captchaSize = !isLargeDesktop ? "compact" : "normal";

  return (
    <div className="scha__register-free register-desktop">
      <PageTitle title={t("page.registerFree.title")} />

      <Box sx={{ display: "flex", flexFlow: "column", gap: "32px" }}>
        <div className="scha__register-free__already">
          <span>{t("page.registerFree.already")} </span>
          <Link onClick={openLogin} className="scha__register-free__already__link">
            {t("page.registerFree.already-link")}
          </Link>
        </div>

        {showCustomerRegistration && <CustomerRegistrationBanner />}

        <div className="scha__register-free">
          <div className="scha__register-free__main-content">
            <div className="scha__register-free__main-content__left">
              <MediasRegistrationBanner />
            </div>
            <form className="scha__register-free__main-content__right" onSubmit={onSubmitHandler}>
              <Card>
                <h3 className="scha__register-free__main-content__right__title">{t("page.registerFree.title")}</h3>

                <div className={"pb-3"}>
                  <Controller
                    name={"email"}
                    control={control}
                    render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                      <MaterialInput
                        label={t("page.registerFree.label")}
                        error={!!error}
                        colorHelperOnError={true}
                        helperLabel={<HelperLabelErrorMessage error={error} />}
                        data-test-id={"register-for-free-email"}
                        inputRef={ref}
                        {...inputProps}
                      />
                    )}
                    rules={{
                      required: { value: true, message: "error.invalidEmail" },
                      pattern: { value: EMAIL_PATTERN, message: "error.invalidEmail" },
                    }}
                  />
                </div>

                <div className="mb-2">
                  <Controller
                    name={"generalTerms"}
                    control={control}
                    rules={{ required: { value: true, message: "error.tos" } }}
                    render={({ field: { value, onChange } }) => (
                      <CheckboxWithLabel
                        data-test-id={"register-for-free__general-terms"}
                        checked={value}
                        onCheck={onChange}
                        centerCheckbox={false}
                      >
                        {t("page.registerFree.terms-part1")}{" "}
                        <a
                          className="scha__register-free__main-content__right__checkbox-area__text__link"
                          href={TERMS_OF_USE_PAGE}
                        >
                          {t("page.registerFree.terms-link")}
                        </a>{" "}
                        {t("page.registerFree.terms-part2")}
                      </CheckboxWithLabel>
                    )}
                  />
                  <ErrorMessage className={"mt-2"} error={errors.generalTerms} />
                </div>

                <div className="mb-2">
                  <Controller
                    name={"privacyPolicy"}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <MarketingConsentCheckbox
                        data-test-id={"register-for-free__privacy-policy"}
                        checked={value}
                        onCheck={onChange}
                        centerCheckbox={false}
                      />
                    )}
                  />
                </div>

                <div className="scha__register-free__main-content__right__mandatory">
                  {t("page.registerFree.mandatory")}
                </div>

                {CAPTCHA_ENABLED && (
                  <Controller
                    name={"captcha"}
                    control={control}
                    rules={{ required: { value: true, message: "recaptcha.challenge.field.empty" } }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <RecaptchaWidget
                        key={captchaSize}
                        onChange={onChange}
                        error={error?.message ? t(error.message) : ""}
                        size={captchaSize}
                      />
                    )}
                  />
                )}

                <Button
                  className={"mt-4"}
                  startIcon={<DefaultButtonIcon type={IconType.User} />}
                  fullWidth={true}
                  data-test-id={"register-for-free__submit"}
                  onClick={onSubmitHandler}
                >
                  {t("page.registerFree.register")}
                </Button>
              </Card>
            </form>
            {loginPopupOpen && <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />}
            {resetPopupOpen && <ResetPasswordPopup initialEmail={watch("email")} close={closeReset} />}
          </div>
          <RegisterContactSalesSection />
        </div>
      </Box>
    </div>
  );
};

type SpanProps = JSX.IntrinsicElements["span"];

interface HelperLabelErrorMessageProps extends SpanProps {
  error?: FieldError;
}

const HelperLabelErrorMessage = ({ error, ...rest }: HelperLabelErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return null;
  }

  return <span {...rest}>{t(error.message)}</span>;
};

interface ErrorMessageProps extends Omit<MessageProps, "type" | "children" | "message"> {
  error?: FieldError;
}

const ErrorMessage = ({ error, ...rest }: ErrorMessageProps) => {
  const { t } = useTranslation();

  if (!error?.message) {
    return null;
  }

  return (
    <Alert severity={"error"} {...rest}>
      {t(error.message)}
    </Alert>
  );
};

export const RegisterContactSalesSection = () => (
  <p className={"scha__register-free__contact-sales"}>
    <Trans i18nKey={"page.registerFree.contactSales"}>
      Are you already a Schaeffler customer or sales partner? Please contact your Schaeffler sales representative for
      free access to <span className={"scha__register-free__contact-sales__logo"}>medias</span>{" "}
      <span className={"scha__register-free__contact-sales__logo2"}>Business</span>.
    </Trans>
  </p>
);

export default memo(RegisterForFreeDesktop);
